import { Button, Group } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { ChecklistList } from 'src/Components/Checklists/ChecklistList';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import { useCreateChecklist } from 'src/Hooks/Mutations/useCreateChecklist';
import useChecklistTemplates from 'src/Hooks/Queries/useChecklistTemplates';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { trackChecklistTemplateUsed } from 'src/Services/Mixpanel/events';
import { ChecklistTemplateDto } from 'src/types/checklist-template.types';

import styles from './ChecklistsMainPage.module.scss';
import { ChecklistTemplatesSection } from './ChecklistTemplatesSection';

const ChecklistsMainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: asset } = useFetchAssetDetails();
  const { data: templates } = useChecklistTemplates();

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('checklist');
  const { hasPermission: canCreate } = useCanCreate('checklist');

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const { mutate: createChecklistFromTemplate } = useCreateChecklist();

  const createChecklist = useCallback(() => {
    if (!asset) return;

    navigate('new');
  }, [asset, navigate]);

  const useTemplateAsIs = useCallback(
    (template: ChecklistTemplateDto) => {
      createChecklistFromTemplate({
        title: template.title,
        // `items` is required by the backend but missing @ApiProperty() on
        // CreateChecklistDto, so the generated client's type omits it — cast until fixed upstream.
        ...({ items: template.items.map(label => ({ label })) } as Record<string, unknown>),
        templateKey: template.key,
        icon: template.icon,
        color: template.color,
      });
      trackChecklistTemplateUsed({ template_key: template.key, mode: 'as_is' });
    },
    [createChecklistFromTemplate]
  );

  const customizeTemplate = useCallback(
    (template: ChecklistTemplateDto) => {
      trackChecklistTemplateUsed({ template_key: template.key, mode: 'customized' });
      navigate('new', { state: { template } });
    },
    [navigate]
  );

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topLine}>
        <div className={styles.titleColumn}>
          <div className={styles.title}>{t('checklists.title', 'Checklists')}</div>
          <div className={styles.subtitle}>
            {t('checklist.subtitle', 'Retrouvez ici toutes les checklists')}
          </div>
        </div>
        <Group>
          <VideoTutorialButton videoUrl={'https://youtu.be/fHuhx6_8sJM'} />
          {canCreate && (
            <Button
              onClick={() => createChecklist()}
              leftSection={<IconPlus size={20} />}
              disabled={assetAboveQuotas}
            >
              {t('checklist.addChecklist', 'Ajouter une checklist')}
            </Button>
          )}
        </Group>
      </div>
      {canCreate && (
        <ChecklistTemplatesSection
          templates={templates}
          disabled={assetAboveQuotas}
          onUseAsIs={useTemplateAsIs}
          onCustomize={customizeTemplate}
        />
      )}
      <ChecklistList />

      <Outlet />
    </div>
  );
};

export default ChecklistsMainPage;
